import React, { useEffect, useState } from 'react';
import { Box, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { spaces } from '../../../assets/styles/themes/tokens';
import LabelledTextField from '../../LabelledTextField/LabelledTextField';
import { ascii, password } from '../../../validators';
import useRouter from '../../../utils/useRouter';
import services from '../../../services';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { UserByTokenResponse, UserResponseData } from '@kimuno/api-sdk/lib/types/responses/User';
import { login as loginAction } from '../../../actions/auth.actions';
import { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import useAction from '../../../utils/useAction';
import { NameAndPasswordSubmitted, ProfileCreationStarted } from '../../../ampli';
import OnboardingStepPasswordImage from '../../../assets/onboarding/onboarding-step-password.svg';
import ErrorConfirmationPage from './ErrorConfirmationPage';
import OnboardingStepImage from '../OnboardingStepImage';
import OnboardingStepContainer from '../OnboardingStepContainer';

interface OwnProps {
  setOnboardingStep: (step: keyof OnboardingSteps) => void
}

interface CreatePasswordPayload {
  firstName: string;
  lastName?: string;
  password: string;
}

const createPasswordFormValidator = yup.object<CreatePasswordPayload>({
  firstName: ascii.required('Required field. Please enter your name.'),
  lastName: ascii,
  password: password(),
});

const OnboardingStepPassword: React.FC<OwnProps> = ({ setOnboardingStep }) => {
  const { t } = useTranslation();
  const login = useAction(loginAction);
  const { match } = useRouter();
  const [isValidToken, setValidToken] = useState<boolean | null>(null);
  const { token } = match.location.query;
  const {
    formState,
    handleSubmit,
    register,
    errors,
  } = useForm<CreatePasswordPayload>({ mode: 'onBlur', validationSchema: createPasswordFormValidator });
  const [user, setUser] = useState<UserByTokenResponse | undefined>();

  const contentHeights = {
    default: 'calc(100vh - 575px)',
    smallScreen: 'calc(100vh - 354px)',
  };

  useEffect(() => {
    services.user.getUserByToken({ optInToken: token })
      .then((user) => {
        setUser(user);
        setValidToken(user.hasValidToken);
      })
      .catch(() => setValidToken(false));
  }, [token]);

  useEffect(() => {
    if (user?.hasValidToken && user?.basicUserInfo) {
      services.track.ampliEventTrack({
        event: new ProfileCreationStarted(),
        userProperties: {
          id: user.basicUserInfo.id,
          type: user.basicUserInfo.type,
          licenceId: user.basicUserInfo.licenceModelId,
          createdAt: user.basicUserInfo.createdAt,
          signUpMethod: user.basicUserInfo.registrationMethod,
        }
      });
    }
  }, [user]);

  const title = t('Welcome to meinUnterricht!');
  const subtitle = t('You can get started right away. First, well help you personalize your profile.');

  const handleOnSubmit = handleSubmit((data) => {
    const payload = {
      data: {
        firstName: data.firstName,
        lastName: data.lastName,
      },
      password: data.password,
      passwordRepeat: data.password,
    };

    services.registration.confirm(token, payload)
      .then(async (result: UserResponseData) => {
        await login(result.email, data.password);
        services.track.ampliEventTrack({ event: new NameAndPasswordSubmitted(), userProperties: { id: result._id } });
      }).then(() => {
        setOnboardingStep('roles');
      });
  });
  
  if (isValidToken === null) {
    return <></>;
  } else if (isValidToken === false) {
    return <ErrorConfirmationPage />;
  }

  return (
    <>
      <OnboardingStepContainer
        title={title}
        subtitle={subtitle}
        stepNum={0}
        contentHeights={contentHeights}
        onNext={() => handleOnSubmit()}
        onNextIsDisabled={formState.isSubmitting || !isValidToken}
      >
        <Box
          component='form'
          data-testid='onboarding-page-form-password'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box>
            <FormControl fullWidth sx={{ gap: spaces.md }}>
              <LabelledTextField
                dataTestId='onboarding-page-form-first-name-input'
                name='firstName'
                label={t('First Name *')}
                placeholder={t('eg Max')}
                inputRef={register}
                error={errors.firstName}
              />
              <LabelledTextField
                dataTestId='onboarding-page-form-last-name-input'
                name='lastName'
                label={t('Last Name')}
                placeholder={t('eg Mustermann')}
                inputRef={register}
                error={errors.lastName}
              />
              <LabelledTextField
                dataTestId='onboarding-page-form-password-input'
                name='password'
                label={t('Create your password *')}
                placeholder={t('Minimum 6 characters')}
                isPasswordTextField
                inputRef={register}
                error={errors.password}
              />
            </FormControl>
          </Box>
        </Box>
      </OnboardingStepContainer>
      <OnboardingStepImage stepImage={<OnboardingStepPasswordImage />}/>
    </>
  );
};

export default OnboardingStepPassword;
